import React, { useEffect, useState, useRef, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { fontStyle, spacing, breakpoints, themeLight } from '@naf/theme';
import { nafColor } from '@nafcore/theme';
import { propValidationConfig } from './propValidationConfig';
// Table
export const Table = ({
  children,
  maxWidth,
  minWidth = '340px',
  className
}) => {
  const outerRef = useRef();
  const tableRef = useRef(null);
  const [hasOverflowRight, setHasOverflowRight] = useState(false);
  const [hasOverflowLeft, setHasOverflowLeft] = useState(false);
  const checkOverflow = useCallback(() => {
    const outer = outerRef.current;
    if (outer.scrollLeft > 0) {
      setHasOverflowLeft(true);
    } else {
      setHasOverflowLeft(false);
    }
    if (outer.scrollLeft < outer.scrollWidth - outer.clientWidth) {
      setHasOverflowRight(true);
    } else {
      setHasOverflowRight(false);
    }
  }, []);
  useEffect(() => {
    checkOverflow();
  }, [checkOverflow]);
  useEffect(() => {
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow]);
  useEffect(() => {
    const outer = outerRef.current;
    outer.addEventListener('scroll', checkOverflow);
    return () => outer.removeEventListener('scroll', checkOverflow);
  }, [checkOverflow]);
  return /*#__PURE__*/React.createElement(Wrap, {
    maxWidth: maxWidth,
    className: className
  }, /*#__PURE__*/React.createElement(Outer, {
    ref: outerRef,
    hasOverflowLeft: hasOverflowLeft,
    hasOverflowRight: hasOverflowRight
  }, /*#__PURE__*/React.createElement(TableContainer, {
    ref: tableRef,
    minWidth: minWidth
  }, children), /*#__PURE__*/React.createElement(OverflowIndicator, {
    left: true,
    visible: hasOverflowLeft
  }), /*#__PURE__*/React.createElement(OverflowIndicator, {
    right: true,
    visible: hasOverflowRight
  })));
};

// TableRow

export const TableRow = ({
  children,
  onClick
}) => /*#__PURE__*/React.createElement(Tr, {
  onClick: onClick
}, children);

// TableHead
export const TableHead = ({
  children
}) => /*#__PURE__*/React.createElement(THead, null, children);

// TableBody
export const TableBody = ({
  children
}) => /*#__PURE__*/React.createElement(TBody, null, children);

// TableCell

export const TableCell = ({
  children,
  as = 'td',
  align
}) => /*#__PURE__*/React.createElement(Td, {
  as: as,
  align: align
}, children);
export default Table;
const Wrap = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "table__Wrap",
  componentId: "sc-cxy6x6-0"
})(["position:relative;", ""], ({
  maxWidth
}) => maxWidth && css(["max-width:", ";"], maxWidth));
const Outer = styled.div.withConfig({
  displayName: "table__Outer",
  componentId: "sc-cxy6x6-1"
})(["width:100%;overflow-x:auto;overflow-y:hidden;"]);
const TableContainer = styled.table.withConfig(propValidationConfig).withConfig({
  displayName: "table__TableContainer",
  componentId: "sc-cxy6x6-2"
})(["width:100%;display:table;table-layout:fixed;border-spacing:0;border-collapse:collapse;min-width:", ";"], props => props.minWidth);
const OverflowIndicator = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "table__OverflowIndicator",
  componentId: "sc-cxy6x6-3"
})(["position:absolute;bottom:0;right:0;width:", ";z-index:2;opacity:", ";transition:opacity 0.3s ease;pointer-events:none;height:100%;", " ", ""], spacing.space80, ({
  visible
}) => visible ? 1 : 0, ({
  left
}) => left && css(["left:0;background:linear-gradient(to right,#fff 1%,rgba(255,255,255,0) 100%);"]), ({
  right
}) => right && css(["right:0;background:linear-gradient(to left,#fff 1%,rgba(255,255,255,0) 100%);"]));
const Tr = styled.tr.withConfig(propValidationConfig).withConfig({
  displayName: "table__Tr",
  componentId: "sc-cxy6x6-4"
})(["display:table-row;box-sizing:border-box;", ""], ({
  onClick
}) => onClick && css(["&:hover{cursor:pointer;background-color:", ";}"], nafColor.signature.yellow10));
const THead = styled.thead.withConfig({
  displayName: "table__THead",
  componentId: "sc-cxy6x6-5"
})(["display:table-header-group;tr{border-bottom:1px solid;border-color:", ";}"], ({
  theme
}) => theme.border ? theme.border.default : themeLight.border.default);
const TBody = styled.tbody.withConfig({
  displayName: "table__TBody",
  componentId: "sc-cxy6x6-6"
})(["display:table-row-group;tr:not(:last-child){border-bottom:1px solid;border-color:", ";}"], ({
  theme
}) => theme.border ? theme.border.subtle : themeLight.border.subtle);
const Td = styled.td.withConfig(propValidationConfig).withConfig({
  displayName: "table__Td",
  componentId: "sc-cxy6x6-7"
})(["display:table-cell;padding:", " 0;text-align:left;", ";box-sizing:border-box;", " ", ""], spacing.space16, ({
  align
}) => `text-align: ${align}` || 'left', ({
  as
}) => as === 'td' && css(["", ";@media (max-width:", "){", ";}"], fontStyle.article.articleText, breakpoints.s, fontStyle.bodyText.bodyText), ({
  as
}) => as === 'th' && css(["", ";@media (max-width:", "){", ";}"], fontStyle.article.articleTextHeader, breakpoints.s, fontStyle.bodyText.bodyTextHeader));
//# sourceMappingURL=table.js.map